'use client'
import {
  ContentfulMedia_default
} from "./chunk-IOBZ5NBW.mjs";
import {
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/components/cards/IconItem.tsx
import { Button, Typography } from "@wbk/ui";
import { Link, useTranslation } from "@wbk/config";
import { ChevronIcon } from "@wbk/svg";
import { twMerge } from "tailwind-merge";
import { jsx, jsxs } from "react/jsx-runtime";
var CardIconItem = ({ card, className, btnProps }) => {
  var _a;
  const { t } = useTranslation("calendar_common");
  const href = (_a = card.button) == null ? void 0 : _a.href;
  const isSoon = !href || href === "#";
  return /* @__PURE__ */ jsxs(
    Button,
    __spreadProps(__spreadValues({
      theme: "secondary",
      as: Link,
      href,
      className: twMerge("group h-full w-full flex-col items-start p-4 text-start", className),
      disabled: isSoon
    }, btnProps), {
      children: [
        card.icon && /* @__PURE__ */ jsx(
          ContentfulMedia_default,
          {
            media: card.icon,
            "aria-disabled": isSoon,
            className: "h-12 w-12 transition duration-500 aria-disabled:opacity-50 group-hover:aria-[disabled=false]:translate-x-4 group-focus:aria-[disabled=false]:translate-x-4 rtl:group-hover:aria-[disabled=false]:-translate-x-4 rtl:group-focus:aria-[disabled=false]:-translate-x-4"
          }
        ),
        /* @__PURE__ */ jsxs(
          "div",
          {
            "aria-disabled": isSoon,
            className: "flex items-center gap-2 transition duration-500 group-hover:aria-[disabled=false]:translate-x-4 group-focus:aria-[disabled=false]:translate-x-4 rtl:group-hover:aria-[disabled=false]:-translate-x-4 rtl:group-focus:aria-[disabled=false]:-translate-x-4",
            children: [
              /* @__PURE__ */ jsx(Typography, { variant: isSoon ? "body-L" : "link-L", className: "inline", children: card.title }),
              isSoon ? /* @__PURE__ */ jsx(Typography, { color: "tertiary", children: t("calendar_common:soon") }) : /* @__PURE__ */ jsx(ChevronIcon, { className: "h-3 w-3 shrink-0 rtl:rotate-180" })
            ]
          }
        )
      ]
    })
  );
};
var IconItem_default = CardIconItem;

// src/components/events/Sellers.tsx
import { useTranslation as useTranslation2 } from "@wbk/config";
import { Button as Button2, Typography as Typography2 } from "@wbk/ui";
import { FlatTicketIcon } from "@wbk/svg";
import { twMerge as twMerge2 } from "tailwind-merge";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var Sellers = ({ sellers, icon = true, className = "", link }) => {
  var _a;
  const { t } = useTranslation2("calendar_common");
  if (!sellers || !((_a = sellers == null ? void 0 : sellers.items) == null ? void 0 : _a.length))
    return null;
  return /* @__PURE__ */ jsxs2("div", { className: twMerge2("text-text-secondary flex items-start gap-0.5", className), children: [
    icon && /* @__PURE__ */ jsx2(FlatTicketIcon, { className: "h-4 w-5" }),
    /* @__PURE__ */ jsx2(Typography2, { variant: "body-XS", className: "shrink-0", children: t("calendar_common:tickets_from") }),
    /* @__PURE__ */ jsx2("div", { className: "flex flex-wrap gap-1 px-0.5", children: sellers.items.map((seller) => {
      const activeClick = link && seller.href;
      const Component = activeClick ? Button2 : "div";
      return /* @__PURE__ */ jsx2("div", { className: "flex items-start", children: /* @__PURE__ */ jsxs2(
        Component,
        {
          as: "a",
          target: "_blank",
          rel: "noreferrer",
          href: seller.href,
          className: twMerge2("text-text-secondary flex p-0", className),
          shape: "text",
          children: [
            /* @__PURE__ */ jsx2(Typography2, { variant: activeClick ? "link-XS" : "body-XS", children: seller.title }),
            activeClick && seller.logo && /* @__PURE__ */ jsx2(
              ContentfulMedia_default,
              {
                className: "mx-2 max-h-5 w-auto max-w-10 object-contain",
                media: seller.logo
              }
            )
          ]
        }
      ) }, seller.id);
    }) })
  ] });
};
var Sellers_default = Sellers;

// src/components/events/card/Location.tsx
import { twMerge as twMerge3 } from "tailwind-merge";
import { jsx as jsx3 } from "react/jsx-runtime";
var EventItemLocation = ({ location, className }) => {
  const text = (location == null ? void 0 : location.city) || "";
  if (!text)
    return null;
  return /* @__PURE__ */ jsx3("p", { className: twMerge3("line-clamp-1 text-sm", className), children: text });
};
var Location_default = EventItemLocation;

// src/components/events/card/Type.tsx
import { useMemo } from "react";
import { formatDate } from "@wbk/utils";
import { Typography as Typography3 } from "@wbk/ui";
import { useRouter } from "@wbk/config";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var EventItemType = ({ className, event, showDate = true }) => {
  const { locale } = useRouter();
  const date = useMemo(() => {
    if (!event.schedule)
      return "";
    if (event.schedule.text) {
      return event.schedule.text;
    }
    if (!event.schedule.openDateTime)
      return "";
    const startDate = formatDate(event.schedule.openDateTime, locale, {
      month: "short",
      day: "2-digit",
      timeZone: void 0
    });
    if (!event.schedule.closeDateTime || event.schedule.openDateTime.split("T")[0] === event.schedule.closeDateTime.split("T")[0]) {
      return startDate;
    }
    const endDate = formatDate(event.schedule.closeDateTime, locale, {
      month: "short",
      day: "2-digit",
      timeZone: void 0
    });
    return `${startDate} - ${endDate}`;
  }, [event.schedule, locale]);
  return /* @__PURE__ */ jsxs3("div", { className: `gap-XXS ${className || ""}`, children: [
    (event == null ? void 0 : event.category) && /* @__PURE__ */ jsxs3(Typography3, { variant: "label-S", className: "inline", style: { color: event.category.color }, children: [
      event == null ? void 0 : event.category.title,
      " ",
      ` ${showDate && date ? " \xB7 " : ""}`
    ] }),
    showDate && date && /* @__PURE__ */ jsx4(Typography3, { className: "inline", variant: "label-S", children: date })
  ] });
};
var Type_default = EventItemType;

// src/components/events/card/Price.tsx
import { useTranslation as useTranslation3 } from "@wbk/config";
import { Typography as Typography4 } from "@wbk/ui";
import { CurrencyPriceLabel } from "@wbk/currency";
import { twMerge as twMerge4 } from "tailwind-merge";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var EventItemPrice = ({ event, className }) => {
  var _a;
  const { t } = useTranslation3("calendar_common");
  const price = event.price;
  if (typeof price !== "number")
    return null;
  return /* @__PURE__ */ jsxs4("div", { className: twMerge4("flex items-center gap-1", className), children: [
    price > 0 && /* @__PURE__ */ jsx5(Typography4, { variant: "body-S", color: "text-primary", children: t("calendar_common:starts_from") }),
    /* @__PURE__ */ jsx5(Typography4, { as: "div", variant: "label-M", color: "text-primary", children: /* @__PURE__ */ jsx5(CurrencyPriceLabel, { originalPrice: price, currency: (_a = event == null ? void 0 : event.currency) == null ? void 0 : _a.code, zeroAsFree: true }) })
  ] });
};
var Price_default = EventItemPrice;

export {
  Sellers_default,
  Location_default,
  Type_default,
  Price_default,
  IconItem_default
};
//# sourceMappingURL=chunk-3RIFEIGI.mjs.map